import { useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Sparkles,
  Wand2,
  BookOpen,
  Palette,
  Type,
  Layout,
  Loader2,
  Check,
  Image as ImageIcon,
  RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ALBUM_FORMATS = [
  { value: '30x30', label: '30x30 cm (kwadrat)' },
  { value: '30x20', label: '30x20 cm (poziom)' },
  { value: '20x30', label: '20x30 cm (pion)' },
  { value: 'A4', label: 'A4' },
];

const DESIGN_STYLES = [
  { value: 'modern', label: 'Nowoczesny', description: 'Minimalistyczny, czyste linie' },
  { value: 'classic', label: 'Klasyczny', description: 'Elegancki, ponadczasowy' },
  { value: 'romantic', label: 'Romantyczny', description: 'Miekkie kolory, delikatne detale' },
  { value: 'bold', label: 'Odwazny', description: 'Mocne kontrasty, duze zdjecia' },
  { value: 'vintage', label: 'Vintage', description: 'Retro estetyka, ciepłe tony' },
];

export default function MagicDesigner({ galleryId, photos = [], onLayoutGenerated }) {
  const [step, setStep] = useState(1);
  const [analyzing, setAnalyzing] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generatingCover, setGeneratingCover] = useState(false);
  const [photoAnalysis, setPhotoAnalysis] = useState(null);
  const [layout, setLayout] = useState(null);
  const [coverUrl, setCoverUrl] = useState(null);

  const [settings, setSettings] = useState({
    page_count: 20,
    format: '30x30',
    topic: '',
    style: 'modern',
    color_scheme: '',
    font_preference: '',
    additional_notes: '',
  });

  const handleAnalyzePhotos = async () => {
    setAnalyzing(true);
    try {
      const response = await axios.post(`${API}/galleries/${galleryId}/analyze_photos`);
      setPhotoAnalysis(response.data);
      toast.success('Analiza zdjec zakonczona');
      setStep(2);
    } catch (error) {
      console.error('Error analyzing photos:', error);
      toast.error('Nie udalo sie przeanalizowac zdjec');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleGenerateLayout = async () => {
    setGenerating(true);
    try {
      const response = await axios.post(`${API}/galleries/${galleryId}/generate_layout`, settings);
      setLayout(response.data);
      toast.success('Layout albumu wygenerowany');
      setStep(3);
      if (onLayoutGenerated) onLayoutGenerated(response.data);
    } catch (error) {
      console.error('Error generating layout:', error);
      toast.error('Nie udalo sie wygenerowac layoutu');
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerateCover = async () => {
    setGeneratingCover(true);
    try {
      const response = await axios.post(`${API}/galleries/${galleryId}/generate_cover`, {
        style: settings.style,
        topic: settings.topic,
      });
      setCoverUrl(response.data.cover_url);
      toast.success('Okladka wygenerowana');
    } catch (error) {
      console.error('Error generating cover:', error);
      toast.error('Nie udalo sie wygenerowac okladki');
    } finally {
      setGeneratingCover(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Progress Steps */}
      <div className="flex items-center justify-center gap-4 mb-8">
        {[1, 2, 3].map((s) => (
          <div key={s} className="flex items-center gap-2">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                step >= s
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground'
              }`}
            >
              {step > s ? <Check className="w-4 h-4" /> : s}
            </div>
            <span className={`text-sm ${step >= s ? 'text-foreground' : 'text-muted-foreground'}`}>
              {s === 1 && 'Analiza'}
              {s === 2 && 'Ustawienia'}
              {s === 3 && 'Generowanie'}
            </span>
            {s < 3 && <div className={`w-12 h-0.5 ${step > s ? 'bg-primary' : 'bg-muted'}`} />}
          </div>
        ))}
      </div>

      {/* Step 1: Photo Analysis */}
      <AnimatePresence mode="wait">
        {step === 1 && (
          <motion.div
            key="step1"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
          >
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-primary" />
                  Analiza zdjec AI
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-muted-foreground">
                  AI przeanalizuje {photos.length} zdjec w galerii i stworzy opisy, ktore pomoga
                  w automatycznym ukladaniu albumu.
                </p>

                <div className="grid grid-cols-6 gap-2">
                  {photos.slice(0, 12).map((photo) => (
                    <div key={photo.id} className="aspect-square rounded overflow-hidden">
                      <img
                        src={photo.thumbnail_url || photo.url}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ))}
                  {photos.length > 12 && (
                    <div className="aspect-square rounded bg-muted flex items-center justify-center">
                      <span className="text-sm text-muted-foreground">+{photos.length - 12}</span>
                    </div>
                  )}
                </div>

                <Button onClick={handleAnalyzePhotos} disabled={analyzing} className="w-full">
                  {analyzing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Analizowanie zdjec...
                    </>
                  ) : (
                    <>
                      <Wand2 className="w-4 h-4 mr-2" />
                      Rozpocznij analize AI
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Step 2: Settings */}
        {step === 2 && (
          <motion.div
            key="step2"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
          >
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Layout className="w-5 h-5 text-primary" />
                  Ustawienia albumu
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Photo Analysis Results */}
                {photoAnalysis && (
                  <div className="p-4 bg-muted/50 rounded-lg space-y-2">
                    <h4 className="text-sm font-medium">Wyniki analizy:</h4>
                    <div className="flex flex-wrap gap-2">
                      {photoAnalysis.suggested_topics?.map((topic, idx) => (
                        <Badge key={idx} variant="secondary">{topic}</Badge>
                      ))}
                    </div>
                    {photoAnalysis.detected_theme && (
                      <p className="text-sm text-muted-foreground">
                        Wykryty motyw: {photoAnalysis.detected_theme}
                      </p>
                    )}
                  </div>
                )}

                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label>Liczba stron</Label>
                    <Input
                      type="number"
                      min="10"
                      max="100"
                      value={settings.page_count}
                      onChange={(e) => setSettings({ ...settings, page_count: parseInt(e.target.value) || 20 })}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Format albumu</Label>
                    <Select
                      value={settings.format}
                      onValueChange={(v) => setSettings({ ...settings, format: v })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ALBUM_FORMATS.map((f) => (
                          <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Temat/Tytul albumu</Label>
                  <Input
                    placeholder="np. Wesele Ani i Tomka, Wakacje 2024"
                    value={settings.topic}
                    onChange={(e) => setSettings({ ...settings, topic: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Styl designu</Label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {DESIGN_STYLES.map((style) => (
                      <div
                        key={style.value}
                        className={`p-3 rounded-lg border cursor-pointer transition-all ${
                          settings.style === style.value
                            ? 'border-primary bg-primary/5'
                            : 'border-border hover:border-primary/50'
                        }`}
                        onClick={() => setSettings({ ...settings, style: style.value })}
                      >
                        <div className="font-medium text-sm">{style.label}</div>
                        <div className="text-xs text-muted-foreground">{style.description}</div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Dodatkowe uwagi (opcjonalnie)</Label>
                  <Textarea
                    placeholder="Np. preferowane kolory, specjalne wymagania..."
                    value={settings.additional_notes}
                    onChange={(e) => setSettings({ ...settings, additional_notes: e.target.value })}
                    rows={3}
                  />
                </div>

                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setStep(1)}>
                    Wstecz
                  </Button>
                  <Button onClick={handleGenerateLayout} disabled={generating} className="flex-1">
                    {generating ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Generowanie layoutu...
                      </>
                    ) : (
                      <>
                        <BookOpen className="w-4 h-4 mr-2" />
                        Wygeneruj layout albumu
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Step 3: Results */}
        {step === 3 && layout && (
          <motion.div
            key="step3"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            className="space-y-4"
          >
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-primary" />
                  Wygenerowany layout
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Global Design */}
                {layout.global_design && (
                  <div className="p-4 bg-muted/50 rounded-lg space-y-2">
                    <h4 className="text-sm font-medium flex items-center gap-2">
                      <Palette className="w-4 h-4" />
                      Styl globalny
                    </h4>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-muted-foreground">Kolor glowny: </span>
                        <span className="font-medium">{layout.global_design.primary_color}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Czcionka: </span>
                        <span className="font-medium">{layout.global_design.font_family}</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Pages Preview */}
                <div className="space-y-2">
                  <h4 className="text-sm font-medium">Strony ({layout.pages?.length || 0})</h4>
                  <div className="grid grid-cols-4 sm:grid-cols-6 gap-2 max-h-64 overflow-y-auto">
                    {layout.pages?.map((page, idx) => (
                      <div
                        key={idx}
                        className="aspect-square bg-muted rounded border border-border p-1 text-xs"
                      >
                        <div className="font-medium">S. {idx + 1}</div>
                        <div className="text-muted-foreground truncate">
                          {page.photo_ids?.length || 0} zdjec
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setStep(2)}>
                    Zmien ustawienia
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleGenerateCover}
                    disabled={generatingCover}
                  >
                    {generatingCover ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <ImageIcon className="w-4 h-4 mr-2" />
                    )}
                    Wygeneruj okladke AI
                  </Button>
                  <Button onClick={() => setStep(1)} className="flex-1">
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Rozpocznij od nowa
                  </Button>
                </div>

                {/* Cover Preview */}
                {coverUrl && (
                  <div className="mt-4 p-4 bg-muted/50 rounded-lg">
                    <h4 className="text-sm font-medium mb-2">Wygenerowana okladka:</h4>
                    <img
                      src={coverUrl}
                      alt="Cover"
                      className="max-w-xs rounded shadow-lg"
                    />
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
